import { Component, DestroyRef, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { CellFocusedEvent, Column, GridApi, IRowNode, RowEditingStartedEvent, RowEditingStoppedEvent, RowNode, RowPinnedType, StartEditingCellParams } from 'ag-grid-enterprise';
import { finalize, Subscription } from 'rxjs';
import { isNotNullOrEmpty } from '../../../../common/functions/is-not-null-or-empty.function';
import { DisclosureValidationStatement } from '../../../../common/models/disclosures/disclosure-validation-statement.model';
import { DisclosureValidationIdType } from '../../../models/disclosure-validation-id-type.enum';
import { InlineEditorActionTriggerParams } from '../../../models/inline-editor-cell-renderer/inline-editor-action-trigger-params.model';
import { IInlineEditorCellRendererParams } from '../../../models/inline-editor-cell-renderer/inline-editor-cell-renderer-params.model';
import { InlineEditorTriggerActionType } from '../../../models/inline-editor-cell-renderer/inline-editor-trigger-action-type.enum';
import { NgClass } from '@angular/common';
import { IconButtonComponent } from '../../../../popper/components/icon-button/icon-button.component';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { IconButtonColorEnumPipe } from '../../../../popper/components/icon-button/icon-button-color-enum.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

let nextInlineEditorCellMessageId = 0;

@Component({
    selector: 'inline-editor-cell-renderer',
    templateUrl: './inline-editor-cell-renderer.component.html',
    styleUrls: ['./inline-editor-cell-renderer.component.scss'],
    standalone: true,
    imports: [IconButtonComponent, NgClass, IconSpriteModule, IconButtonColorEnumPipe]
})
export class InlineEditorCellRendererComponent implements ICellRendererAngularComp, OnDestroy {
  private static lastTabShift = false;
  private static tabDirectionTrackerActive = false;

  private readonly instanceId = ++nextInlineEditorCellMessageId;
  public readonly errorMessageId = `inline-editor-cell-error-${this.instanceId}`;
  public readonly warningMessageId = `inline-editor-cell-warning-${this.instanceId}`;
  // This component's own elementRef isn't in the DOM during agInit, but params.eParentOfValue
  // is ag-grid's own cell wrapper element and is already attached - use it to reach the
  // ancestor .ag-cell instead. See "ag-grid cell element timing" memory.
  private agCellEl: HTMLElement | null = null;

  private cellName: string = '';
  public params: IInlineEditorCellRendererParams;
  public data: any;
  public originalData: any;
  public currentStateOfData: any;
  public api: GridApi<any>;
  public readOnly: boolean = true;
  public canDelete: boolean = false;
  public onlyHandleDelete: boolean = false;
  public canAdd: boolean = true;
  public canSave: boolean = true;
  public canRenew: boolean = false;
  public allowMultipleOngoingEdits: boolean = false;
  private columnToStartEditKey: string = '0';
  private currentlyAdding: boolean = false;
  public disclosureValidationStatements: Array<DisclosureValidationStatement> = [];

  @ViewChildren(IconButtonComponent) public cellIcons: QueryList<IconButtonComponent>;
  // .bind(this) creates a new function reference every time it's called, so removeEventListener
  // must be given the SAME bound reference that was passed to addEventListener - bind once here
  // and reuse it in both agInit and ngOnDestroy.
  private readonly boundRowEditingStartedHandler = this.rowEditingStartedEventHandler.bind(this);
  private readonly boundCellFocusedHandler = this.cellFocusedEventHandler.bind(this);

  constructor(private destroyRef: DestroyRef) {
  }

  ngOnDestroy(): void {
    if (this.api) {
      if (this.allowMultipleOngoingEdits) {
        this.api.removeEventListener("rowEditingStarted", this.boundRowEditingStartedHandler);
      }
      this.api.removeEventListener("cellFocused", this.boundCellFocusedHandler);
      //else {
      //  this.api.removeEventListener("rowEditingStopped", this.rowEditingStoppedEventHandler.bind(this));
      //}
    }
  }

  rowEditingStoppedEventHandler(event: RowEditingStartedEvent): void {
    let itemId = this.params.itemRowIdGetter(this.params);
    if (itemId == event.node.id) {
      this.stopEditing(true, true);
      //event.event.stopImmediatePropagation();
    }
  }

  rowEditingStartedEventHandler(event: RowEditingStartedEvent): void {
    let itemId = this.params.itemRowIdGetter(this.params);
    if (itemId == event.node.id)
      this.startEditing(true);
  }

  public onButtonTabKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const icons = this.cellIcons.filter(icon => !icon.disabled);
    if (icons.length <= 1) return;
    const currentIndex = icons.findIndex(icon => icon.button.nativeElement === document.activeElement);
    if (currentIndex === -1) return;
    // At boundaries, do nothing — suppressKeyboardEvent returns false there,
    // leaving ag-grid to navigate to the next/prev cell.
    if (!event.shiftKey && currentIndex === icons.length - 1) return;
    if (event.shiftKey && currentIndex === 0) return;
    event.preventDefault();
    event.stopPropagation();
    const next = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
    icons[next].focusIn();
  }

  cellFocusedEventHandler(event: CellFocusedEvent): void {
    let colId = this.params.column.getColId();
    let pinned = this.params.node.rowPinned as RowPinnedType;
    let rowIndex = this.params.node.rowIndex;
    let eventColId = (event.column as Column).getColId();
    if (rowIndex == event.rowIndex && eventColId == colId && pinned == event.rowPinned
      && this.cellIcons && this.cellIcons.length > 0) {
      const icons = this.cellIcons.filter(icon => !icon.disabled);
      if (icons.length === 0) return;
      const target = InlineEditorCellRendererComponent.lastTabShift ? icons[icons.length - 1] : icons[0];
      target.focusIn();
    }
  }

  rowIsBeingEdited(): boolean {
    let editingCells = this.params.api.getEditingCells();
    if (editingCells && editingCells.length > 0 && editingCells.some(x => x.rowIndex == this.params.node.rowIndex))
      return true;
    else
      return false;
  }

  public agInit(params: IInlineEditorCellRendererParams): void {
    if (!InlineEditorCellRendererComponent.tabDirectionTrackerActive) {
      document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Tab') InlineEditorCellRendererComponent.lastTabShift = e.shiftKey;
      }, true);
      InlineEditorCellRendererComponent.tabDirectionTrackerActive = true;
    }

    this.data = params.data;
    this.currentStateOfData = JSON.parse(JSON.stringify(params.data));
    this.api = params.api;
    this.cellName = params?.colDef?.cellRenderer?.toString() || '';
    this.params = params;
    this.agCellEl = (params.eParentOfValue as HTMLElement)?.closest('.ag-cell') as HTMLElement;

    if (this.params.allowMultipleOngoingEdits) {
      this.allowMultipleOngoingEdits = this.params.allowMultipleOngoingEdits
    }

    if (params.currentlyAddingChangedEvent) {
      params.currentlyAddingChangedEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((currentlyAdding) => {
        this.currentlyAdding = currentlyAdding;
      }, error => {
        console.log('Error listening for updated validation info ' + error);
      });
    }
    
    if (params.validityChangedEvent) {
      let validityChanged$ = params.validityChangedEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: Array<DisclosureValidationStatement>) => {
        this.params.disclosureValidationStatements = result;
        this.filterDisclosureValidation();
      }, error => {
        console.log('Error listening for updated validation info ' + error);
      });
    }

    if (params.dataChangedEvent/* && this.params.node.rowPinned*/) {
      let dataChanged$ = params.dataChangedEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: { value: any, field: string, itemId?: string }) => {
        if ((isNotNullOrEmpty(event.itemId) && this.params.itemRowIdGetter(this.params) == event.itemId) ||
          (params.node.rowPinned == "bottom" && event.itemId == "")) {
          this.currentStateOfData[event.field] = event.value;
          if (this.params.canAdd !== null && this.params.canAdd !== undefined) {
            this.canAdd = this.params.canAdd instanceof Function ? this.params.canAdd(this.currentStateOfData) : this.params.canAdd;
          }

          if (this.params.canSave !== null && this.params.canSave !== undefined) {
            this.canSave = this.params.canSave instanceof Function ? this.params.canSave(this.currentStateOfData) : this.params.canSave;
          }
        }
      }, error => {
        console.log('Error listening for updated validation info ' + error);
      });
    }

    this.filterDisclosureValidation();

    this.originalData = JSON.parse(JSON.stringify(this.data));

    if (params.columnToStartEditKey !== null && params.columnToStartEditKey !== undefined)
      this.columnToStartEditKey = params.columnToStartEditKey;

    let isRowPinned = this.params.node.rowPinned;

    if (this.api) {
      if (this.allowMultipleOngoingEdits) {
        this.api.addEventListener("rowEditingStarted", this.boundRowEditingStartedHandler);
      }
      this.api.addEventListener("cellFocused", this.boundCellFocusedHandler);
      //else {
      //  this.api.addEventListener("rowEditingStopped", this.rowEditingStoppedEventHandler.bind(this));
      //}
    }

    if (this.params.onlyHandleDelete !== null && this.params.onlyHandleDelete !== undefined) {
      this.onlyHandleDelete = this.params.onlyHandleDelete;
    }

    if (this.params.canDelete !== null && this.params.canDelete !== undefined) {
      this.canDelete = this.params.canDelete instanceof Function ? this.params.canDelete(params) : this.params.canDelete;
    }

    if (this.params.canAdd !== null && this.params.canAdd !== undefined) {
      this.canAdd = this.params.canAdd instanceof Function ? this.params.canAdd(params.data) : this.params.canAdd;
    }

    if (this.params.canSave !== null && this.params.canSave !== undefined) {
      this.canSave = this.params.canSave instanceof Function ? this.params.canSave(params.data) : this.params.canSave;
    }

    if (this.params.canRenew !== null && this.params.canRenew !== undefined) {
      this.canRenew = this.params.canRenew instanceof Function ? this.params.canRenew(params.data) : this.params.canRenew;
    }

    this.readOnly = params.isBeingEdited ? !params.isBeingEdited(params) : true;
  
  }

  filterDisclosureValidation() {
    //Filter out statements that don't pertain to our data type
    let itemId = this.params.itemRowIdGetter(this.params);
    if (this.params.idType) {
      switch (this.params.idType) {
        case DisclosureValidationIdType.Purpose:
          this.disclosureValidationStatements = this.params.disclosureValidationStatements.filter(x => x.purposeId == itemId && (x.ingredientsId == null || x.ingredientsId == undefined));
          break;
        case DisclosureValidationIdType.Ingredient:
          this.disclosureValidationStatements = this.params.disclosureValidationStatements.filter(x => x.ingredientsId == itemId);
          break;
        case DisclosureValidationIdType.WaterSource:

          break;
      }
    }

    this.updateAriaOnCell();
  }

  private updateAriaOnCell(): void {
    if (!this.agCellEl) return;

    const describedByIds: string[] = [];
    if (this.hasError()) describedByIds.push(this.errorMessageId);
    if (this.hasWarning()) describedByIds.push(this.warningMessageId);

    if (this.hasError()) {
      this.agCellEl.setAttribute('aria-invalid', 'true');
    } else {
      this.agCellEl.removeAttribute('aria-invalid');
    }

    if (describedByIds.length > 0) {
      this.agCellEl.setAttribute('aria-describedby', describedByIds.join(' '));
    } else {
      this.agCellEl.removeAttribute('aria-describedby');
    }
  }

  public refresh(params: any): boolean {
    return true;
  }

  public startEditing(hereFromEventListener: boolean = false): void {

    if (this.params.onActionTriggerHandler) {
      this.params.onActionTriggerHandler({
        context: this.params.context,
        row: this.params.node.rowIndex,
        rowId: this.params.itemRowIdGetter(this.params),
        action: InlineEditorTriggerActionType.StartedEditingRow,
        data: this.data
      } as InlineEditorActionTriggerParams);


      if (this.params.isBeingEdited !== undefined && this.params.isBeingEdited !== null) {
        this.readOnly = !this.params.isBeingEdited(this.params);
      }
    }

    if (!hereFromEventListener) {
      //Needed to get style rules to refresh and disable non edited rows.
      this.api.redrawRows();
      this.api.startEditingCell({
        rowIndex: this.params.node.rowIndex,
        colKey: this.columnToStartEditKey,
        rowPinned: this.params.node.rowPinned
      } as StartEditingCellParams);
    }

    console.log('end of togglereadonly Purpose', this.readOnly);
  }

  revertData(): void {
    if ((this.originalData || this.params.originalData)) {
      if (this.params.originalData != null && this.params.originalData != undefined) {
        if (this.params.originalData instanceof Function) {
          var paramData = this.params.originalData(this.currentStateOfData);
          if (paramData)
            this.originalData = JSON.parse(JSON.stringify(this.params.originalData(this.currentStateOfData)));
        }
        else {
          this.originalData = JSON.parse(JSON.stringify(this.params.originalData));
        }
      }

      let rowId = this.params.itemRowIdGetter(this.params);
      let rowNode: IRowNode<any>;

      if (this.params.node.rowPinned == 'bottom') {
        rowNode = (this.api as GridApi).getPinnedBottomRow(this.params.node.rowIndex);
      }
      else if (this.params.node.rowPinned == 'top') {
        rowNode = (this.api as GridApi).getPinnedTopRow(this.params.node.rowIndex);
      }
      else {
        rowNode = (this.api as GridApi).getRowNode(rowId);
      }

      rowNode.data = JSON.parse(JSON.stringify(this.originalData));
      this.params.data = JSON.parse(JSON.stringify(this.originalData));
      this.currentStateOfData = JSON.parse(JSON.stringify(this.originalData));
      //Reset the canAddVariable, if it's a pinned row, which is the only type of row to show the add button
      if (this.params.node.rowPinned == 'bottom' || this.params.node.rowPinned == 'top') {
        if (this.params.canAdd !== null && this.params.canAdd !== undefined) {
          this.canAdd = this.params.canAdd instanceof Function ? this.params.canAdd(this.currentStateOfData) : this.params.canAdd;          
        }
      }
      if ((this.params.node as RowNode).detailNode) {
        (this.params.node as RowNode).detailNode.data = JSON.parse(JSON.stringify(this.originalData));
      }
    }
    else {

    }

  }

  public stopEditing(revertData: boolean = true, hereFromEventHandler: boolean = false): void {
    //this.readOnly = true;

    if (revertData) {
      this.revertData();
    }

    if (this.params.onActionTriggerHandler) {
      this.params.onActionTriggerHandler({
        context: this.params.context,
        row: this.params.node.rowIndex,
        rowId: this.params.itemRowIdGetter(this.params),
        action: InlineEditorTriggerActionType.StoppedEditingRow,
        data: this.data
      } as InlineEditorActionTriggerParams);
    }

    if (this.params.isBeingEdited !== undefined && this.params.isBeingEdited !== null) {
      this.readOnly = !this.params.isBeingEdited(this.params);
    }

    if (!hereFromEventHandler) {
      this.api.stopEditing(revertData);
      //Needed to get style rules to refresh and re-enable non edited rows.
      this.api.redrawRows();
    }
  }

  public add(): void {
    this.stopEditing(false, false);

    if (this.params.onActionTriggerHandler && !this.currentlyAdding) {
      //Prevent multiple calls to add by say holding down the enter key
      this.currentlyAdding = true;
      this.params.onActionTriggerHandler({
        context: this.params.context,
        row: this.params.node.rowIndex,
        rowId: this.params.itemRowIdGetter(this.params),
        action: InlineEditorTriggerActionType.AddItemAsNewRow,
        data: JSON.parse(JSON.stringify(this.currentStateOfData))
      } as InlineEditorActionTriggerParams);
    }

    this.revertData();
  }

  public save(): void {
    this.stopEditing(false, false);
    if (this.params.onActionTriggerHandler) {
      this.params.onActionTriggerHandler({
        context: this.params.context,
        row: this.params.node.rowIndex,
        rowId: this.params.itemRowIdGetter(this.params),
        action: InlineEditorTriggerActionType.SaveRow,
        data: this.data
      } as InlineEditorActionTriggerParams);
    }
  }

  public deleteRow(): void {
    this.readOnly = true;
    this.api.stopEditing(true);

    if (this.params.onActionTriggerHandler) {
      this.params.onActionTriggerHandler({
        context: this.params.context,
        row: this.params.node.rowIndex,
        rowId: this.params.itemRowIdGetter(this.params),
        action: InlineEditorTriggerActionType.DeleteRow,
        data: this.data
      } as InlineEditorActionTriggerParams);
    }
  }

  public renew(): void {
    if (this.params.onActionTriggerHandler) {
      this.params.onActionTriggerHandler({
        context: this.params.context,
        row: this.params.node.rowIndex,
        rowId: this.params.itemRowIdGetter(this.params),
        action: InlineEditorTriggerActionType.RenewRow,
        data: this.data
      } as InlineEditorActionTriggerParams);
    }
  }

  hasError(): boolean {
    if (this.disclosureValidationStatements && this.disclosureValidationStatements.length > 0) {
      let errorExists = this.disclosureValidationStatements.filter(x => x.messageType == "Error" || x.messageType == "Critical Error").length > 0;
      if (errorExists)
        return true;
    }
    return false;
  }

  hasWarning(): boolean {
    if (this.disclosureValidationStatements && this.disclosureValidationStatements.length > 0) {
      let warningExists = this.disclosureValidationStatements.filter(x => x.messageType == "Warning").length > 0;
      if (warningExists)
        return true;
    }
    return false;
  }

  getErrorOrWarningMessage(type: string) {
    let message = "";

    if (this.hasError && type == "validationError") {
      let errorStatements = this.disclosureValidationStatements.filter(x => x.messageType == "Error" || x.messageType == "Critical Error");
      errorStatements.forEach(x => message += x.message + " ");
    }
    else if (this.hasError && type == "validationWarning") {
      let warningStatements = this.disclosureValidationStatements.filter(x => x.messageType == "Warning")
      warningStatements.forEach(x => message += x.message + " ");
    }

    return message;
  }
}
